import React from 'react';

import { formatSeparateByThousands } from 'Helpers/formatService';

/**
 * Span of control and org depth, measured from the reporting lines recorded on
 * the positions themselves.
 *
 * Labelled as measured, not inferred: these are counted from `supervisor_id` —
 * the same relation the org chart draws — rather than implied from level counts.
 * The stored `avg_spans` / `max_spans` / `positions_levels` fields are
 * deliberately not used; they measure staff contracts per position, which is a
 * different thing entirely.
 *
 * Where no position in scope reports to another in scope, the panel says so.
 * Printing 0.0 would claim a measured flat structure where the truth is that
 * nobody has recorded who reports to whom.
 */
function Figure({ label, value, hint }) {
    return (
        <div className="tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-border-grey">
            <div className="tw-text-sm tw-text-blue-75 tw-font-medium tw-mb-2">{label}</div>
            <div className="tw-text-2.5xl tw-text-charcoal tw-font-semibold">{value}</div>
            {!!hint && <div className="tw-text-xs tw-text-charcoal-50 tw-mt-1">{hint}</div>}
        </div>
    );
}

export default function PeopleSpansAndLayers({ spansAndLayers }) {
    if (!spansAndLayers) {
        return null;
    }

    if (!spansAndLayers.has_reporting_lines) {
        return (
            <div>
                <p className="tw-text-sm tw-text-charcoal-75 tw-mb-1">
                    {$t('No position in scope reports to another position in scope, so span of control cannot be measured.')}
                </p>
                <p className="tw-text-xs tw-text-charcoal-50 tw-mb-0">
                    {$t('Set a supervisor on a position to build the reporting structure. Positions supervised from outside this scope count as roots here.')}
                </p>
            </div>
        );
    }

    return (
        <div>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-x-5 tw-gap-y-3">
                <Figure label={$t('Layers')} value={formatSeparateByThousands(spansAndLayers.layers)} hint={$t('longest chain of command in scope')} />
                <Figure label={$t('Average span')} value={Number(spansAndLayers.avg_span ?? 0).toFixed(1)} hint={$t('reports per position that has any')} />
                <Figure
                    label={$t('Widest span')}
                    value={formatSeparateByThousands(spansAndLayers.max_span ?? 0)}
                    hint={$t('most reports held by one position')}
                />
            </div>
            <p className="tw-text-xs tw-text-charcoal-50 tw-mt-4 tw-mb-0">
                {$t(
                    'Measured from the reporting lines recorded on each position, not inferred from level counts. Positions with no reports of their own are excluded from the average rather than counted as zero.',
                )}
            </p>
        </div>
    );
}
